import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, animate, motion, useInView, useReducedMotion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import Logo from '../components/Logo';
import { ThemeToggle } from '../components/ThemeToggle';
import ParticleBackground from '../components/ParticleBackground';
import { Expandable } from '../components/Expandable';
import posterNyc from '../assets/posters/poster_climb_nyc.jpg';
import posterPottery from '../assets/posters/poster_pottery_london.jpeg';
import posterBooks from '../assets/posters/poster_bookclub_chicago.jpeg';

// venues.outclose.com: the page we send a venue instead of a call. The form
// posts to the marketing server (POST /api/venues/apply), which files it in
// /venues. VITE_API_URL is the Cloud Run origin; empty in dev = vite proxy.
const VENUES_API = import.meta.env.VITE_API_URL ?? '';

const EASE = [0.16, 1, 0.3, 1] as const;

const VIEW = { once: true, margin: '-12% 0px -12% 0px' } as const;

// Below the fold: rise once, when the block scrolls into view.
function useRise() {
  const reduce = useReducedMotion();
  return (d = 0) =>
    reduce
      ? { initial: { opacity: 0 }, whileInView: { opacity: 1 }, viewport: VIEW, transition: { duration: 0.5, delay: d } }
      : { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: VIEW, transition: { duration: 0.9, ease: EASE, delay: d } };
}
// First screen: rise on mount.
function useRiseNow() {
  const reduce = useReducedMotion();
  return (d = 0) =>
    reduce
      ? { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.5, delay: d } }
      : { initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.9, ease: EASE, delay: d } };
}

// A figure that counts up from zero the first time it is seen.
function Counter({ to, className }: { to: number; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: '-10% 0px' });
  const reduce = useReducedMotion();
  useEffect(() => {
    const el = ref.current;
    if (!el || !inView) return;
    if (reduce) { el.textContent = to.toLocaleString(); return; }
    const c = animate(0, to, { duration: 1.6, ease: [0.16, 1, 0.3, 1], onUpdate: (v) => { el.textContent = Math.round(v).toLocaleString(); } });
    return () => c.stop();
  }, [inView, to, reduce]);
  return <span ref={ref} className={className}>0</span>;
}

// A money figure whose changed digits pop in from below when the value changes.
function Digits({ value }: { value: number }) {
  const reduce = useReducedMotion();
  const text = '$' + value.toLocaleString();
  const prev = useRef(text);
  const prevText = prev.current;
  useEffect(() => { prev.current = text; }, [text]);
  const chars = text.split('');
  // leading characters shared with the previous value stay still; only the changed tail pops
  let same = 0;
  if (prevText.length === text.length) while (same < text.length && prevText[same] === text[same]) same++;
  return (
    <span className="inline-flex items-baseline tabular-nums" aria-label={text}>
      {chars.map((ch, i) => (
        <AnimatePresence key={`${i}-${chars.length}`} mode="popLayout" initial={false}>
          <motion.span
            key={`${i}-${ch}-${i < same ? 'same' : text}`}
            initial={reduce || i < same ? false : { y: 10, opacity: 0, filter: 'blur(2px)' }}
            animate={{ y: 0, opacity: 1, filter: 'blur(0px)' }}
            exit={reduce ? undefined : { y: -10, opacity: 0, filter: 'blur(2px)' }}
            transition={{ duration: 0.4, ease: [0.34, 1.45, 0.64, 1], delay: i >= same ? (i - same) * 0.04 : 0 }}
            className="inline-block"
          >
            {ch}
          </motion.span>
        </AnimatePresence>
      ))}
    </span>
  );
}

const WORD = {
  hidden: { opacity: 0, y: '0.6em' },
  show: (i: number) => ({ opacity: 1, y: 0, transition: { duration: 0.8, ease: EASE, delay: 0.1 + i * 0.07 } }),
};


function Shell({ children, className = '', id }: { children: React.ReactNode; className?: string; id?: string }) {
  return (
    <div id={id} className={`mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>
      {children}
    </div>
  );
}
function Kicker({ children }: { children: React.ReactNode }) {
  return <span className="inline-block text-sm font-semibold text-primary-500 dark:text-primary-400">{children}</span>;
}
const h2Class = 'mt-5 font-Poppins text-[clamp(1.85rem,4.4vw,3rem)] font-bold leading-[1.14] tracking-[-0.02em] text-[#1c1510] [text-wrap:balance] dark:text-[#f3ece4]';
const bodyClass = 'text-[#473d34] dark:text-[#dccfc0] leading-[1.7] tracking-[0.005em]';

function Header() {
  return (
    <header>
      <Shell className="flex items-center justify-between py-6">
        <Logo />
        <div className="flex items-center gap-4">
          <ThemeToggle />
          <a href="#apply" className="btn-primary text-sm">Set up your venue</a>
        </div>
      </Shell>
    </header>
  );
}

function Hero() {
  const rise = useRiseNow();
  const reduce = useReducedMotion();
  return (
    <section className="relative">
      <Shell className="grid items-center gap-12 pb-16 pt-10 sm:pt-16 lg:grid-cols-[1.1fr_0.9fr] lg:pb-24">
        <div>
          <motion.div {...rise(0)}><Kicker>Outclose for venues</Kicker></motion.div>
          <motion.h1
            {...rise(0.08)}
            className="mt-7 font-Poppins font-bold tracking-[-0.025em] text-[#1c1510] dark:text-[#f3ece4] [text-wrap:balance] text-[clamp(3rem,7vw,6.5rem)] leading-[0.98]"
          >
            {['Your', 'slowest', 'night,'].map((w, i) => (
              <span key={w}>
                <span className="inline-block overflow-hidden pb-[0.08em] -mb-[0.08em] align-bottom">
                  <motion.span className="inline-block" variants={WORD} initial={reduce ? 'show' : 'hidden'} animate="show" custom={i}>{w}</motion.span>
                </span>{' '}
              </span>
            ))}
            <span className="relative inline-block whitespace-nowrap text-primary-500 overflow-visible">
              <span className="inline-block overflow-hidden pb-[0.08em] -mb-[0.08em] align-bottom">
                <motion.span className="inline-block" variants={WORD} initial={reduce ? 'show' : 'hidden'} animate="show" custom={3}>full.</motion.span>
              </span>
              <svg aria-hidden viewBox="0 0 200 14" preserveAspectRatio="none" className="absolute -bottom-2 left-0 h-3 w-full text-primary-500/60">
                <motion.path
                  d="M2 9 C 50 3, 150 3, 198 8" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round"
                  initial={reduce ? { pathLength: 1 } : { pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.9, ease: EASE, delay: 0.7 }}
                />
              </svg>
            </span>
          </motion.h1>
          <motion.p {...rise(0.16)} className={`mt-8 max-w-[52ch] text-lg sm:text-xl ${bodyClass}`}>
            We bring a group to a weeknight slot you'd usually have open, pay you for every seat, and handle everything except the activity. You set the group size. No calls. One form.
          </motion.p>
          <motion.div {...rise(0.24)} className="mt-10 flex flex-wrap items-center gap-4">
            <a href="#apply" className="btn-primary group inline-flex items-center gap-2 px-8 py-3 text-base">
              Set up your venue
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </a>
            <a href="#how" className="font-Poppins font-semibold text-[#1c1510] dark:text-[#f3ece4] hover:text-primary-500">How it works</a>
          </motion.div>
        </div>

        {/* Poster fan */}
        <div className="relative mx-auto h-[340px] w-full max-w-md sm:h-[420px]">
          {[
            { src: posterPottery, cls: 'left-0 top-10', rot: -6, z: 'z-10', delay: 0.25, float: 5 },
            { src: posterNyc, cls: 'left-1/2 top-0 -translate-x-1/2', rot: 2, z: 'z-20', delay: 0.4, float: 7 },
            { src: posterBooks, cls: 'right-0 top-12', rot: 6, z: 'z-10', delay: 0.55, float: 6 },
          ].map((p) => (
            <motion.div
              key={p.src}
              className={`absolute ${p.cls} ${p.z} w-[58%]`}
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 80, rotate: p.rot + 10, scale: 0.92 }}
              animate={{ opacity: 1, y: 0, rotate: p.rot, scale: 1 }}
              transition={{ duration: 1.1, ease: EASE, delay: p.delay }}
              whileHover={reduce ? undefined : { y: -10, scale: 1.04, rotate: p.rot / 2, zIndex: 30, transition: { duration: 0.35, ease: EASE } }}
            >
              <motion.img
                src={p.src}
                alt=""
                className="w-full rounded-2xl shadow-2xl shadow-black/40 ring-1 ring-white/10"
                animate={reduce ? undefined : { y: [0, -p.float, 0] }}
                transition={{ duration: 5 + p.float * 0.4, ease: 'easeInOut', repeat: Infinity, delay: p.delay + 1 }}
              />
            </motion.div>
          ))}
        </div>
      </Shell>
    </section>
  );
}

function Stats() {
  const rise = useRise();
  const stats: [React.ReactNode, string][] = [
    [<Counter to={70000} />, 'members'],
    [<Counter to={30} />, 'cities'],
    [<Counter to={8} />, 'countries'],
  ];
  return (
    <section className="border-y border-black/[0.07] dark:border-white/[0.07] bg-black/[0.02] dark:bg-white/[0.03]">
      <Shell className="grid grid-cols-3 gap-8 py-10 text-center">
        {stats.map(([n, l], i) => (
          <motion.div key={l} {...rise(0.05 * i)}>
            <p className="font-Poppins text-3xl font-bold tracking-[-0.02em] text-[#1c1510] dark:text-[#f3ece4] sm:text-4xl">{n}</p>
            <p className={`mt-1 text-sm ${bodyClass}`}>{l}</p>
          </motion.div>
        ))}
      </Shell>
    </section>
  );
}

function HowItWorks() {
  const rise = useRise();
  const steps = [
    ['You give us a slot', 'A weeknight evening, 6:30 to 8:00, for as many as you can take at once. Around 12 for a climbing gym, a few courts\u2019 worth for pickleball. We start with one night and add more as members book.'],
    ['We fill it', 'We sell the seats to our members and send you the headcount the day before. You never chase anyone.'],
    ['They show up together', 'One group, one check-in, waivers signed in advance. Our co-host meets them at your door.'],
    ['We pay you', 'Your group rate for every seat we sold. You never handle our members’ money.'],
  ];
  return (
    <section id="how">
      <Shell className="py-20 sm:py-28">
        <motion.div {...rise(0)}>
          <Kicker>How it works</Kicker>
          <h2 className={h2Class}>Four steps. Three of them are ours.</h2>
        </motion.div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map(([h, p], i) => (
            <motion.div
              key={h}
              {...rise(0.08 * i)}
              whileHover={{ y: -6, transition: { duration: 0.3, ease: EASE } }}
              className="group rounded-3xl border border-black/[0.07] bg-white/60 p-6 transition-colors duration-300 hover:border-primary-500/40 dark:border-white/[0.07] dark:bg-white/[0.04]"
            >
              <p className="font-Poppins text-5xl font-bold tabular-nums text-primary-500/70 transition-colors duration-300 group-hover:text-primary-500">{i + 1}</p>

              <p className="mt-4 font-Poppins text-xl font-semibold text-[#1c1510] dark:text-[#f3ece4]">{h}</p>
              <p className={`mt-2 ${bodyClass}`}>{p}</p>
            </motion.div>
          ))}
        </div>
      </Shell>
    </section>
  );
}

function Worth() {
  const rise = useRise();
  const [rate, setRate] = useState(18);
  const [seats, setSeats] = useState(12);
  const [nights, setNights] = useState(2);
  const month = rate * seats * nights;
  return (
    <section className="bg-primary-500 text-white">
      <Shell className="grid items-center gap-12 py-20 sm:py-24 lg:grid-cols-2">
        <motion.div {...rise(0)}>
          <span className="inline-block text-sm font-semibold text-white/80">What it's worth</span>
          <h2 className="mt-5 font-Poppins text-[clamp(1.85rem,4.4vw,3rem)] font-bold leading-[1.14] tracking-[-0.02em] [text-wrap:balance]">
            A dead Wednesday becomes the best night of your week.
          </h2>
          <p className="mt-6 max-w-[48ch] text-white/85 leading-[1.7]">
            You set the group rate. We fill the seats. Nights grow with demand, so drag the numbers and see what a slot you already have could be worth.
          </p>
        </motion.div>
        <motion.div {...rise(0.1)} className="rounded-3xl bg-white/10 p-6 ring-1 ring-white/20 backdrop-blur sm:p-8">
          {[
            ['Group rate per person', rate, 10, 60, 1, setRate, '$'],
            ['Seats per night', seats, 6, 20, 1, setSeats, ''],
            ['Nights per month', nights, 1, 8, 1, setNights, ''],
          ].map(([label, val, min, max, step, set, pre]) => (
            <div key={label as string} className="mb-5">
              <div className="flex items-center justify-between text-sm font-semibold">
                <span>{label as string}</span>
                <span className="tabular-nums">{pre as string}{val as number}</span>
              </div>
              <input
                type="range"
                min={min as number}
                max={max as number}
                step={step as number}
                value={val as number}
                onChange={(e) => (set as (n: number) => void)(Number(e.target.value))}
                className="mt-2 w-full accent-white"
              />
            </div>
          ))}
          <div className="mt-6 border-t border-white/20 pt-5">
            <p className="text-sm text-white/80">Paid to you in a month like that</p>
            <p className="font-Poppins text-5xl font-bold tabular-nums tracking-[-0.02em]"><Digits value={month} /></p>
            <p className="mt-1 text-sm text-white/70">from a slot that was empty.</p>
          </div>
        </motion.div>
      </Shell>
    </section>
  );
}

function GiveGet() {
  const rise = useRise();
  const get = ['A full block on a slow night', 'Paid for every seat we sell', 'New regulars who found you through us', 'Zero admin. No calls, no chasing'];
  const need = ['A weeknight slot you can hold', 'A group rate per person, rentals included', 'Your waiver link, if you use one', 'Where to send the money'];
  return (
    <section>
      <Shell className="pt-20 pb-4 sm:pt-28 sm:pb-6">
        <div className="grid gap-6 lg:grid-cols-2">
          {[['What you get', get, 'text-emerald-500'], ['What we need', need, 'text-primary-500']].map(([title, items, color], i) => (
            <motion.div key={title as string} {...rise(0.08 * i)} className="rounded-3xl border border-black/[0.07] bg-white/60 p-8 dark:border-white/[0.07] dark:bg-white/[0.04]">
              <h3 className="font-Poppins text-2xl font-bold tracking-[-0.01em] text-[#1c1510] dark:text-[#f3ece4]">{title as string}</h3>
              <ul className="mt-5 space-y-3">
                {(items as string[]).map((t, j) => (
                  <motion.li
                    key={t}
                    className={`flex gap-3 ${bodyClass}`}
                    initial={{ opacity: 0, x: -12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={VIEW}
                    transition={{ duration: 0.6, ease: EASE, delay: 0.25 + 0.08 * i + 0.07 * j }}
                  >
                    <motion.span initial={{ scale: 0.4, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }} viewport={VIEW} transition={{ duration: 0.5, ease: [0.34, 1.45, 0.64, 1], delay: 0.35 + 0.08 * i + 0.07 * j }}>
                      <Check className={`mt-1 h-5 w-5 shrink-0 ${color as string}`} />
                    </motion.span>
                    {t}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
        <motion.div {...rise(0.2)} className="mt-12">
          <Kicker>Booking now</Kicker>
          <div className="mt-4 flex flex-wrap gap-2">
            {['Climbing', 'Boxing', 'Pottery', 'Life drawing', 'Pickleball or padel', 'Book club'].map((a, j) => (
              <motion.span
                key={a}
                initial={{ opacity: 0, scale: 0.85, y: 8 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                viewport={VIEW}
                whileHover={{ y: -3, transition: { duration: 0.2 } }}
                transition={{ duration: 0.5, ease: [0.34, 1.45, 0.64, 1], delay: 0.1 + j * 0.06 }}
                className="rounded-full border border-black/10 px-4 py-2 font-Poppins text-sm font-semibold text-[#1c1510] transition-colors hover:border-primary-500 hover:text-primary-500 dark:border-white/15 dark:text-[#f3ece4]"
              >{a}</motion.span>
            ))}
          </div>
        </motion.div>
      </Shell>
    </section>
  );
}

const FAQ: { q: string; a: string }[] = [
  { q: 'What is Outclose?', a: 'An app that brings small groups of LGBTQ+ strangers together, mostly for dinner. 70,000 members, 30 cities. Groups is the same idea around an activity.' },
  { q: 'How big is the group?', a: 'As big as you tell us. A climbing gym usually takes 10 to 15, a six-court pickleball place takes 24. You set the cap on the form and we sell up to it, with a waitlist behind it. If more people want in than you can hold, we open a second slot or a second date instead of squeezing.' },
  { q: "What's a group rate?", a: 'Your normal walk-in price with a discount for a guaranteed block. Most venues land 20 to 30 percent under list. You tell us the number on the form.' },
  { q: 'Who pays?', a: 'Members pay Outclose. We pay you the group rate for every seat we sold, so you never handle anyone\u2019s money at the desk. Tell us on the form how you want to be paid.' },
  { q: 'How often?', a: 'It starts with one night. When a group sells, we add nights, and the busiest groups run every week. You only ever hold dates you\u2019ve agreed to.' },
  { q: "What if it doesn't fill?", a: "If we're under half full 48 hours out, we release the slot and tell you, so you can sell it. You're never left holding an empty block." },
  { q: "Who's on the night?", a: 'Our members and one co-host, who arrives early, greets people and keeps the group together. Your staff run the activity as normal.' },
  { q: 'Waivers and rentals?', a: 'Send us your waiver link and we put it in the booking flow, so everyone has signed before they arrive. Rentals sit inside the group rate, no add-ons at the desk.' },
  { q: 'Do we need to do anything in the app?', a: 'No. Everything comes to you by email: the slot confirmation, the headcount, and the statement.' },
  { q: 'Can we stop?', a: "Any time. Dates we've already sold go ahead, and nothing is booked after that." },
];

function Faq() {
  const rise = useRise();
  return (
    <Shell className="py-20 sm:py-28">
      <motion.div {...rise(0)}>
        <Kicker>FAQ</Kicker>
        <h2 className={h2Class}>Questions GMs ask.</h2>
      </motion.div>
      <motion.div {...rise(0.1)} className="mt-12 flex flex-col gap-4">
        {FAQ.map((f) => (
          <Expandable key={f.q} title={f.q}>{f.a}</Expandable>
        ))}
      </motion.div>
    </Shell>
  );
}

function Field({ label, name, placeholder, type = 'text', required }: { label: string; name: string; placeholder: string; type?: string; required?: boolean }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5">{label}</label>
      <input name={name} type={type} placeholder={placeholder} required={required} className="input h-12" />
    </div>
  );
}

function Apply() {
  const rise = useRise();
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [second, setSecond] = useState<'yes' | 'no' | ''>('');

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const text = (k: string) => String(f.get(k) ?? '').trim() || null;
    if (!second) { setError('Tell us whether anyone else books group events.'); return; }
    setSending(true);
    setError(null);
    try {
      const res = await fetch(`${VENUES_API}/api/venues/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          venue: text('venue'), name: text('name'), title: text('title'), email: text('email'), phone: text('phone'),
          secondContact: second === 'yes' ? { name: text('secondName'), email: text('secondEmail'), title: text('secondTitle') } : null,
          city: text('city'), activity: text('activity'), weeknights: f.getAll('weeknights').map(String),
          maxPerSlot: text('maxPerSlot') ?? undefined, walkInPrice: text('walkInPrice'), groupRate: text('groupRate'),
          waiverUrl: text('waiverUrl'), payoutMethod: text('payoutMethod'), website: text('website'), notes: text('notes'),
          company: String(f.get('company') ?? ''),
        }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.success) throw new Error(json?.error ?? 'Something went wrong. Email venues@outclose.com and we will sort it.');
      setSent(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSending(false);
    }
  }

  return (
    <section id="apply" className="border-t border-black/[0.07] dark:border-white/[0.07]">
      <Shell className="grid gap-12 py-20 sm:py-28 lg:grid-cols-[0.8fr_1.2fr]">
        <motion.div {...rise(0)}>
          <Kicker>Two minutes</Kicker>
          <h2 className={h2Class}>Set up your venue.</h2>
          <p className={`mt-6 max-w-[40ch] ${bodyClass}`}>Tell us the slot, the rate and how you want to be paid. We propose a first date by email within a day, and that's the whole setup.</p>
          <p className={`mt-6 text-sm ${bodyClass}`}>Questions? venues@outclose.com. We reply the same day.</p>
        </motion.div>
        <motion.div {...rise(0.1)} className="rounded-3xl border border-black/[0.07] bg-white/70 p-6 dark:border-white/[0.07] dark:bg-white/[0.04] sm:p-8">
          {sent ? (
            <div className="py-10 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-300"><Check className="h-7 w-7" /></div>
              <p className="font-Poppins text-2xl font-bold text-[#1c1510] dark:text-[#f3ece4]">Got it.</p>
              <p className={`mt-2 ${bodyClass}`}>You'll hear from us by email within a day with a first date.</p>
            </div>
          ) : (
            <form className="space-y-4" onSubmit={submit}>
              {/* Honeypot: people never see it, bots fill it. */}
              <input name="company" tabIndex={-1} autoComplete="off" aria-hidden className="hidden" />
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Venue" name="venue" placeholder="Hollywood Boulders" required />
                <Field label="City" name="city" placeholder="Los Angeles" required />
                <Field label="Your name" name="name" placeholder="Isabel" required />
                <Field label="Your title" name="title" placeholder="Owner, events manager, front desk" required />
                <Field label="Email" name="email" placeholder="isabel@venue.com" type="email" required />
                <Field label="Phone (optional)" name="phone" placeholder="+1" />
                <Field label="Website (optional)" name="website" placeholder="venue.com" />
              </div>
              <fieldset>
                <legend className="block text-sm font-medium mb-1.5">Is anyone else responsible for booking group events?</legend>
                <div className="flex gap-2">
                  {([['yes', 'Yes, add them'], ['no', "No, it's me"]] as const).map(([v, label]) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setSecond(v)}
                      className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${second === v ? 'border-primary-600 bg-primary-500 text-white' : 'border-neutral-300 bg-white dark:border-neutral-600 dark:bg-neutral-800'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </fieldset>
              {second === 'yes' && (
                <div className="grid gap-4 rounded-2xl border border-black/[0.07] p-4 dark:border-white/[0.07] sm:grid-cols-3">
                  <Field label="Their name" name="secondName" placeholder="Marco" required />
                  <Field label="Their email" name="secondEmail" placeholder="marco@venue.com" type="email" required />
                  <Field label="Their title" name="secondTitle" placeholder="Events manager" />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1.5">Activity</label>
                <select name="activity" className="input h-12" defaultValue="" required>
                  <option value="" disabled>Pick one</option>
                  {['Climbing', 'Boxing', 'Pottery', 'Life drawing', 'Pickleball or padel', 'Book club', 'Something else'].map((a) => <option key={a}>{a}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Weeknights you can hold, 6:30 to 8:00</label>
                <div className="flex flex-wrap gap-2">
                  {['Mon', 'Tue', 'Wed', 'Thu'].map((d) => (
                    <label key={d} className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-600">
                      <input type="checkbox" name="weeknights" value={d} className="h-4 w-4 accent-primary-500" /> {d}
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Most you can take in one slot" name="maxPerSlot" placeholder="12" type="number" required />
                <Field label="Walk-in price" name="walkInPrice" placeholder="$24" />
                <Field label="Group rate, rentals in" name="groupRate" placeholder="$18" required />
              </div>
              <Field label="Waiver link (optional)" name="waiverUrl" placeholder="https://" />
              <div>
                <label className="block text-sm font-medium mb-1.5">How should we pay you?</label>
                <select name="payoutMethod" className="input h-12" defaultValue="">
                  <option value="" disabled>Pick one</option>
                  <option>Bank transfer, I'll send details by email</option>
                  <option>We'll invoice you monthly</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Anything we should know? (optional)</label>
                <textarea name="notes" rows={3} maxLength={2000} placeholder="Best nights, how far ahead you book, what a group needs to bring" className="input w-full py-3" />
              </div>
              <label className="flex cursor-pointer items-start gap-3 text-sm">
                <input type="checkbox" required className="mt-0.5 h-4 w-4 shrink-0 accent-primary-500" />
                <span className={bodyClass}>I can hold the slot for the group, release it if we're under half full 48 hours out, and get paid the group rate per seat sold.</span>
              </label>
              <button type="submit" disabled={sending} className="btn-primary group inline-flex w-full items-center justify-center gap-2 py-3 text-base disabled:opacity-60">
                {sending ? 'Sending' : 'Send it over'} <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </button>
              {error && <p className="text-center text-sm text-red-600 dark:text-red-400">{error}</p>}
            </form>
          )}
        </motion.div>
      </Shell>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-black/[0.07] dark:border-white/[0.07]">
      <Shell className="flex flex-col items-start justify-between gap-6 py-12 sm:flex-row sm:items-center">
        <Logo />
        <div className="flex flex-wrap gap-7 text-sm text-[#544a40] dark:text-[#c3b5a6]">
          <a href="https://outclose.com" className="hover:text-primary-500">Outclose</a>
          <a href="https://outclose.com/locations" className="hover:text-primary-500">Locations</a>
          <a href="https://outclose.com/privacy" className="hover:text-primary-500">Privacy</a>
          <a href="mailto:venues@outclose.com" className="hover:text-primary-500">Contact</a>
        </div>
      </Shell>
    </footer>
  );
}

export default function Venues() {
  return (
    <div className="relative min-h-dvh overflow-x-hidden">
      <ParticleBackground className="absolute inset-0" />
      <Header />
      <main>
        <Hero />
        <Stats />
        <HowItWorks />
        <Worth />
        <GiveGet />
        <Faq />
        <Apply />
      </main>
      <Footer />
    </div>
  );
}
